import {ErrorInfo} from 'react';
import {ErrorReporterRequestPayload} from '../server/@types';
/**
 * reportError depends on a crumb available on window.ha
 * as well as a errorReporter.url available on window
 */
type DecoratedWindow = Window & {
    ha?: {
        crumb?: string;
    };
    errorReporterConfig: {url: string};
};

/**
 * Error along with info from a componentDidCatch as emitted from a Error Boundary component in React
 */
type ComponentError = {
    error: Error;
    errorInfo: ErrorInfo;
};

export type ErrorReportData = {
    name: string;
    componentError?: ComponentError;
};

function postError(data: ErrorReportData) {
    const {ha, errorReporterConfig} = (window &&
        ((window as unknown) as DecoratedWindow)) || {
        ha: {},
        errorReporterConfig: {}
    };

    const crumb = (ha && ha.crumb) || '';
    const url = errorReporterConfig.url || '';

    if (!crumb) {
        return Promise.reject('error reporter must be configured for csrf');
    }
    if (!url) {
        return Promise.reject('no error reporter config found');
    }

    const payload: ErrorReporterRequestPayload = {
        name: data.name,
        ...(!!data.componentError && {
            componentError: {
                message: data.componentError.error.message,
                stack: data.componentError.error.stack,
                errorInfo: data.componentError.errorInfo
            }
        })
    };

    return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json;charset=UTF-8',
            'x-csrf-token': crumb
        },
        body: JSON.stringify(payload)
    });
}

const errorTimeout: {[key: string]: NodeJS.Timeout | void} = {};
/**
 * Reports errors from client changes. Debounces error reporting for noisy errors by errReportParams.name
 * @param ErrorReportData params to request be logged as an error report
 */
export default function reportError(data: ErrorReportData): void {
    if (typeof window === 'undefined') {
        return;
    }
    if (errorTimeout[data.name]) {
        return;
    }
    postError(data);

    errorTimeout[data.name] = setTimeout(() => {
        const timeout = errorTimeout[data.name];
        if (timeout) {
            errorTimeout[data.name] = clearTimeout(timeout);
        }
    }, 200);
}
